import { Link } from 'react-router-dom';
import ContactForm from '@/components/feature/ContactForm';
import { SectionHead, Kicker } from '@/components/feature/Atoms';
import { aboutImgs } from '@/mocks/images';
import { stats } from '@/mocks/site';
import { usePageMeta } from '@/hooks/useSeo';

const principles = [
  { icon: 'ri-focus-3-line', title: '记录优先，结论延后', text: '先按时间线记录可溯源的事实，在缺乏新事实前允许结论悬置，不抢跑、不预判。' },
  { icon: 'ri-links-line', title: '多信源交叉核对', text: '关键信息尽量寻找第二来源，区分一手信源与二手转述，宁可慢，不可错。' },
  { icon: 'ri-chat-3-line', title: '并列呈现各方声音', text: '把当事各方的回应并列放上桌面，而不是只呈现单方面叙事，让读者自己判断。' },
  { icon: 'ri-shield-check-line', title: '尊重人格与边界', text: '复盘只针对事实与现象，不针对具体个人开火，守住法律与道德底线。' },
];

const team = [
  { img: aboutImgs.edit1, name: '阿橙', role: '事件脉络 · 主编', desc: '负责时间线与首发信源梳理，信奉“先把顺序排对再谈结论”。' },
  { img: aboutImgs.edit2, name: '小绿', role: '反转观察 · 编辑', desc: '专盯实锤与反转节点，爱做上下文核查与截图溯源。' },
  { img: aboutImgs.edit3, name: '墨默', role: '吃瓜法务 · 审校', desc: '内容上线前把关边界与措辞，主张“围观也要讲分寸”。' },
];

const schema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于99吃瓜',
  description: '99吃瓜的品牌介绍、网站定位、发展理念、内容原则与联系方式。',
};

export default function About() {
  usePageMeta(
    '关于我们 - 99吃瓜',
    '99吃瓜是一家专注事件复盘与反转梳理的中文内容媒体。了解我们的品牌定位、发展理念、内容原则、编辑团队与联系方式。',
  );

  return (
    <div className="py-12 md:py-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <nav aria-label="面包屑" className="mx-auto max-w-[1280px] px-4 md:px-6">
        <ol className="flex items-center gap-1.5 text-xs text-foreground-500">
          <li>
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="text-foreground-700">
            关于我们
          </li>
        </ol>
      </nav>

      <div className="mx-auto mt-8 max-w-[1280px] px-4 md:px-6">
        {/* 头部 */}
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <Kicker>关于我们 · 理性吃瓜</Kicker>
            <h1 className="mt-4 text-3xl font-black leading-snug text-foreground-950 md:text-5xl">
              我们为什么做
              <span className="text-primary-500"> 99吃瓜</span>
            </h1>
            <p className="mt-5 text-base leading-8 text-foreground-600 md:text-lg">
              每一次反转背后，都是一次信息的失真与补全。99吃瓜想做那个愿意多花一点时间、把前因后果讲清楚的内容品牌——不替任何一方定论，只把事实与流程摊开给你看。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              从事件复盘、反转梳理，到各方回应与后续进展，我们用编辑部的方法论，陪你把每一次吃瓜吃得明明白白。
            </p>
          </div>
          <figure className="overflow-hidden rounded-lg border border-background-200">
            <img
              src={aboutImgs.desk}
              alt="99吃瓜编辑部工作台，桌上铺满时间线与事实核查图表的编辑插画"
              className="h-72 w-full object-cover object-top md:h-96"
            />
          </figure>
        </div>

        {/* 数据 + 定位 */}
        <div className="mt-14 grid gap-6 lg:grid-cols-[1fr_2fr]">
          <div className="rounded-lg bg-accent-950 p-7 text-accent-100 md:p-9">
            <h2 className="text-2xl font-black text-background-50">一小段简介</h2>
            <p className="mt-4 text-sm leading-7 text-accent-200/85">
              我们是专注中文互联网公共事件的内容媒体，站内内容以“复盘”为方法、以“理性”为底色，覆盖事件发酵、反转、回应与澄清全链路。
            </p>
            <div className="mt-7 grid grid-cols-2 gap-5">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-heading text-2xl font-black text-primary-400">{s.value}</p>
                  <p className="mt-1 text-xs text-accent-200/80">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {principles.map((p) => (
              <div key={p.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                  <i className={p.icon} aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 编辑团队 */}
        <section className="mt-16">
          <SectionHead kicker="编辑团队" title="在幕后的那些人" desc="他们负责把每一条时间线排对、把每个反转节点查清。" />
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {team.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
                <img
                  src={t.img}
                  alt={`99吃瓜编辑 ${t.name}（${t.role}）头像`}
                  className="mx-auto h-24 w-24 rounded-full object-cover object-top"
                />
                <figcaption>
                  <h3 className="mt-4 text-lg font-bold text-foreground-950">{t.name}</h3>
                  <p className="mt-1 text-xs font-semibold text-primary-600">{t.role}</p>
                  <p className="mt-3 text-sm leading-6 text-foreground-600">{t.desc}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* 联系 */}
        <section className="mt-16 rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
            <div>
              <SectionHead kicker="联系我们" title="想对哪期复盘聊聊看法？" desc="投稿线索、纠错指正、栏目建议都欢迎。收到留言后我们会尽快回复。" />
              <div className="mt-6 space-y-3 text-sm text-foreground-600">
                <p className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-600" aria-hidden="true" />
                  读者邮箱：hello@99chigua.example
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-quill-pen-line text-primary-600" aria-hidden="true" />
                  投稿线索：tougao@99chigua.example
                </p>
              </div>
            </div>
            <ContactForm />
          </div>
        </section>
      </div>
    </div>
  );
}